import {
  PropsWithChildren,
  useCallback,
  useState,
  createContext,
  useContext,
} from "react";
import { useFormContext, UseFormReturn } from "react-hook-form";
import { Form } from "web-design-system";
import { array, object, number, string, z, boolean } from "zod";
import { StepupFrequency } from "creso-core/constants";
import { getProposalStepUp } from "creso-core/utils";

export const ProposalFormSchema = object({
  id: number().optional(),
  name: string(),
  expected_return: string().or(number()).optional(),
  duration_months: number().optional(),
  client: number(),
  include_remarks: boolean(),
  goal: object({
    id: number(),
    name: string(),
    target_corpus: string(),
    expected_inflation: string(),
    duration_months: number(),
    total_current_value: number().nullable().optional(),
    category: string(),
    linked_holdings: array(
      object({
        scheme_isin: string(),
        folio_id: number(),
        folio_number: string(),
      })
    ).optional(),
  }).nullable(),
  include_linked_holdings: z.boolean(),
  total_sip_amount: string(),
  total_lumpsum_amount: string(),
  projected_value: string().nullable(),
  step_up_amount: number().nullable(),
  step_up_percentage: number().nullable(),
  step_up_frequency: z.nativeEnum(StepupFrequency).nullable(),
  proposal_funds: array(
    object({
      id: number().optional(),
      fund_scheme: number(),
      source: z.enum(["NEW", "EXISTING"]),
      source_folio: number().nullable().optional(),
      type: z.enum(["SIP", "LUMPSUM"]),
      sip_amount: string().nullable().optional(),
      lumpsum_amount: string().nullable().optional(),
      remarks: string().optional(),
      order: number().optional(),
      amc_code: string().optional(),
      fund_scheme_name: string().optional(),
      fund_detail: z.any().optional(),
      collection: object({
        name: string(),
        id: number().optional(),
        type: z.enum(["basket", "proposal"]),
      })
        .nullable()
        .optional(),
    })
  ),
}).superRefine((d, ctx) => {
  if (
    d.step_up_frequency &&
    Number(d.total_sip_amount) > 0 &&
    !d.step_up_amount &&
    !d.step_up_percentage
  ) {
    ["step_up_amount", "step_up_percentage"].forEach((path) =>
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Required",
        path: [path],
      })
    );
  }
});

type StepUpFields = {
  step_up_amount?: string | number | null;
  step_up_percentage?: string | number | null;
  step_up_frequency?: StepupFrequency | null;
};

// Step-up override for the backtest API, which takes the whole-proposal amount
export function getBacktestStepUpParams(
  values: StepUpFields,
  sipFundCount: number
) {
  const stepUp = sipFundCount > 0 && getProposalStepUp(values, sipFundCount);
  if (!stepUp) return {};
  return {
    stepUpFrequency: stepUp.frequencyMonths,
    stepUpAmount: stepUp.amount,
    stepUpPercentage: stepUp.percentage,
  };
}

export type ProposalUIState =
  | "init"
  | "setup"
  | "lumpsum"
  | "sip"
  | "remarks"
  | "download"
  | "backtest";

type ProposalBuilderContextType = {
  next: () => void;
  prev: () => void;
  goTo: (state: ProposalUIState) => void;
  uiState: ProposalUIState;
  collectionId?: number;
  backtestPeriod: string;
  backtestBenchmark: string;
  setBacktestPeriod: (period: string) => void;
  setBacktestBenchmark: (benchmark: string) => void;
};

const ProposalBuilderContext = createContext<ProposalBuilderContextType>({
  next: () => {
    throw new Error("Not implemented");
  },
  prev: () => {
    throw new Error("Not implemented");
  },
  goTo: () => {
    throw new Error("Not implemented");
  },
  uiState: "setup",
  collectionId: undefined,
  backtestPeriod: "5Y",
  backtestBenchmark: "NIFTY 500",
  setBacktestPeriod: () => {},
  setBacktestBenchmark: () => {},
});

export function ProposalProvider(
  props: PropsWithChildren<{
    state?: ProposalUIState;
    collectionId?: number;
    form: UseFormReturn<z.infer<typeof ProposalFormSchema>>;
  }>
) {
  const [uiState, setUIState] = useState<ProposalUIState>(
    props.state || "setup"
  );
  const [backtestPeriod, setBacktestPeriod] = useState("5Y");
  const [backtestBenchmark, setBacktestBenchmark] = useState("NIFTY 500");

  const isLumpsumNonZero = Number(props.form.watch("total_lumpsum_amount")) > 0;

  const isSipNonZero = Number(props.form.watch("total_sip_amount")) > 0;

  const goTo = useCallback(
    (state: ProposalUIState) => {
      setUIState(state);
    },
    [setUIState]
  );

  const prev = useCallback(() => {
    if (uiState === "setup") return setUIState("init");
    if (uiState === "lumpsum") return setUIState("setup");
    if (uiState === "sip")
      return setUIState(isLumpsumNonZero ? "lumpsum" : "setup");
    if (uiState === "remarks")
      return setUIState(isSipNonZero ? "sip" : "lumpsum");
    if (uiState === "backtest")
      return setUIState(isSipNonZero ? "sip" : "lumpsum");
    if (uiState === "download") return setUIState("remarks");
    throw new Error("Invalid UI state");
  }, [isLumpsumNonZero, uiState, setUIState, isSipNonZero]);

  const next = useCallback(() => {
    if (uiState === "init") {
      return setUIState("setup");
    }
    if (uiState === "setup")
      return setUIState(isLumpsumNonZero ? "lumpsum" : "sip");
    if (uiState === "lumpsum")
      return setUIState(isSipNonZero ? "sip" : "remarks");
    if (uiState === "sip") return setUIState("remarks");
    if (uiState === "remarks") return setUIState("download");
    throw new Error("Invalid UI state");
  }, [uiState, isLumpsumNonZero, isSipNonZero]);

  return (
    <ProposalBuilderContext.Provider
      value={{
        next,
        prev,
        goTo,
        uiState,
        collectionId: props.collectionId,
        backtestPeriod,
        backtestBenchmark,
        setBacktestPeriod,
        setBacktestBenchmark,
      }}
    >
      <Form {...props.form}>{props.children}</Form>
    </ProposalBuilderContext.Provider>
  );
}

export function useProposalBuilderContext() {
  const context = useContext(ProposalBuilderContext);

  if (!context) {
    throw new Error(
      "useProposalBuilderContext must be used within a ProposalProvider"
    );
  }

  return context;
}

export function useProposalFormContext() {
  return useFormContext<z.infer<typeof ProposalFormSchema>>();
}
